/**
 * Which stacked segment a horizontal bar's total/share label should hang off, per category (row).
 *
 * ECharts positions a label relative to the rect of the data item it belongs to — not to the whole
 * stack. Pinning the label to a fixed series therefore misplaces it whenever that series' segment
 * isn't the bar's outer edge. The visible case is module D: it stacks to the *left* of zero when it
 * is negative, so a `position: "right"` label anchored to it lands on top of the coloured bar
 * instead of after it (dark text over a saturated blue segment, split across stage colours).
 *
 * The label is always drawn to the right of its anchor, so the anchor is whichever segment owns the
 * bar's rightmost edge:
 *
 *  - any positive segment   → last series with a positive value; its right edge is the stack's end
 *  - only negative segments → first series with a negative value; its right edge is the zero line
 *  - all zero               → first series, which also sits on the zero line
 *
 * Anchoring an all-negative bar at zero rather than at its far left end is deliberate: it keeps the
 * label inside the empty half of the row, where it can never collide with the category names on a
 * narrow dashboard card, and it makes the rule uniform — the number always follows the bar.
 *
 * Two properties of this that are deliberate, so please don't "fix" them:
 *
 *  - **Assumes a stack.** "Last positive series" is the bar's outer edge only when the series are
 *    stacked. A caller combining `valueLabel` with `stacked: false` and 2+ series would get one
 *    label, carrying the sum of every series, pinned to a single side-by-side bar. All three real
 *    callers are either single-series or stacked; guard this if that ever stops being true.
 *  - **On a mixed-sign bar the label's value and its position differ.** The text is the row's net
 *    total (positives + negatives), but a stacked bar draws negatives left of zero, so the net is
 *    never a visible edge: A1–A3 120 + C 8 + D −14 draws ink from −14 to 128 and reads "114".
 *    Anchoring at the net instead would land the text at 114 — inside the coloured segments, which
 *    is precisely the unreadable "dark text on a blue bar" this whole change removed. The bar's end
 *    is the only placement that stays outside the ink, so the label reads as a row summary rather
 *    than as a point on the axis.
 */

/**
 * @param seriesValues values per series, each indexed by category — `seriesValues[series][category]`
 * @param categoryCount number of rows on the category axis
 * @returns for each row, the index of the series whose data item carries the label
 */
export function getValueLabelAnchors(seriesValues: number[][], categoryCount: number): number[] {
  return Array.from({ length: categoryCount }, (_, categoryIndex) => {
    let lastPositiveSeries = -1;
    let firstNegativeSeries = -1;

    seriesValues.forEach((values, seriesIndex) => {
      const value = values[categoryIndex] ?? 0;
      if (value > 0) lastPositiveSeries = seriesIndex;
      else if (value < 0 && firstNegativeSeries < 0) firstNegativeSeries = seriesIndex;
    });

    if (lastPositiveSeries >= 0) return lastPositiveSeries;
    if (firstNegativeSeries >= 0) return firstNegativeSeries;
    return 0;
  });
}
